import React, { ReactElement, useContext } from 'react';
import {
  Animated,
  Platform,
  StyleSheet,
  TextStyle,
  TouchableHighlight,
  TouchableNativeFeedback,
  View,
  ViewStyle,
} from 'react-native';

import { animatePressIn, animatePressOut } from '../../theming/Animating';
import Colors from '../../theming/Colors';
import Spacing from '../../theming/Spacing';
import { ThemeContext } from '../../theming/ThemeContext';
import KitIcon, { IconName } from '../KitIcon';
import KitLoader from '../KitLoader';
import KitText from '../KitText';

const defaultIconSize = 20;
const DEFAULT_HEIGHT = 44;

//******************************************************************************
// Types
//******************************************************************************
export interface IProps {
  circle?: boolean;
  onPress?: () => void;
  title?: string;
  disabled?: boolean;
  isLoading?: boolean;
  secondary?: boolean;
  margin?: number;
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
  marginHorizontal?: number;
  marginVertical?: number;
  color?: string;
  borderRadius?: number;
  height?: number;
  iconColor?: string;
  textColor?: string;
  icon?: IconName;
  iconSize?: number;
  red?: boolean;
  white?: boolean;
  style?: ViewStyle;
  titleStyle?: TextStyle;
  small?: boolean;
  testID?: string;
  paddingLeft?: number;
  paddingRight?: number;
  underlayColor?: string;
}

interface IButtonWrapperStyle {
  backgroundColor: string;
  color: string;
}

export default function KitButton({
  circle = false,
  color = undefined,
  iconColor = undefined,
  textColor = undefined,
  disabled = false,
  height,
  borderRadius,
  icon = undefined,
  iconSize = defaultIconSize,
  isLoading = false,
  margin,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  marginHorizontal,
  marginVertical,
  onPress,
  red = false,
  secondary = false,
  small = false,
  style = {},
  testID = '',
  title = '',
  titleStyle = {},
  white = false,
  paddingLeft,
  paddingRight,
  underlayColor,
}: IProps): ReactElement {
  const { colorForScheme } = useContext(ThemeContext);

  const scale = new Animated.Value(1);

  const onPressIn = () => {
    animatePressIn(scale);
  };

  const onPressOut = () => {
    animatePressOut(scale);
  };

  const SharedButton = ({
    children,
  }: {
    children: ReactElement | ReactElement[] | undefined;
  }) => (
    <Animated.View
      style={[
        { transform: [{ scale: scale }], ...style },
        small &&
          Platform.select({
            native: { width: 'auto' },
            web: { alignSelf: 'flex-start' },
          }),
      ]}
    >
      <TouchableHighlight
        style={getSharedButtonWrapperStyle(borderRadius, {
          margin,
          marginTop,
          marginBottom,
          marginLeft,
          marginRight,
          marginHorizontal,
          marginVertical,
        })}
        disabled={disabled}
        onPress={onPress}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        underlayColor={
          underlayColor ||
          colorForScheme?.({ default: Colors.N100 }) ||
          Colors.N100
        }
        delayPressIn={0}
        testID={testID || title}
      >
        {children}
      </TouchableHighlight>
    </Animated.View>
  );

  const AndroidButton = ({
    children,
  }: {
    children: ReactElement | ReactElement[] | undefined;
  }) => (
    <Animated.View
      style={[
        { transform: [{ scale: scale }], ...style },
        small && { alignSelf: 'flex-start' },
      ]}
    >
      <View
        style={getAndroidButtonWrapperStyle(
          borderRadius,
          {
            margin,
            marginTop,
            marginBottom,
            marginLeft,
            marginRight,
            marginHorizontal,
            marginVertical,
          },
          secondary
        )}
      >
        <TouchableNativeFeedback
          disabled={disabled}
          onPress={onPress}
          onPressIn={onPressIn}
          onPressOut={onPressOut}
          useForeground={true}
          delayPressIn={0}
          testID={testID || title}
        >
          <View>{children}</View>
        </TouchableNativeFeedback>
      </View>
    </Animated.View>
  );

  const _getBackgroundColor = () => {
    if (color) {
      return color;
    } else if (secondary) {
      return (
        colorForScheme?.({ dark: Colors.N900, light: Colors.N100 }) ||
        Colors.N100
      );
    } else if (red) {
      return Colors.R500;
    } else if (white) {
      return Colors.N0;
    } else {
      return colorForScheme?.({ default: Colors.brand }) || Colors.brand;
    }
  };

  const _getButtonTextColor = () => {
    if (white) {
      return Colors.N900;
    }

    if (textColor) {
      return textColor;
    }

    if (secondary) {
      return disabled
        ? 'rgba(0,0,0,0.4)'
        : colorForScheme?.({ light: Colors.N900, dark: Colors.N100 }) ||
            Colors.N900;
    } else {
      return disabled ? 'rgba(255,255,255,0.4)' : Colors.N0;
    }
  };

  const Button: any = Platform.select({
    ios: SharedButton,
    android: AndroidButton,
    web: SharedButton,
  });

  const themedStyle: IButtonWrapperStyle = {
    backgroundColor: _getBackgroundColor(),
    color: _getButtonTextColor(),
  };

  return (
    <Button>
      <InnerButton
        backgroundColor={themedStyle.backgroundColor}
        borderRadius={borderRadius}
        iconColor={iconColor}
        color={themedStyle.color}
        disabled={disabled}
        height={height}
        icon={icon}
        iconSize={iconSize}
        isLoading={isLoading}
        secondary={secondary}
        title={title}
        titleStyle={titleStyle}
        small={small}
        paddingLeft={paddingLeft}
        paddingRight={paddingRight}
        circle={circle}
      />
    </Button>
  );
}

//******************************************************************************
// Styles
//******************************************************************************
const styles = StyleSheet.create({
  sharedButtonWrapper: {
    borderRadius: 27,
    margin: 0,
  },
  androidButtonWrapper: {
    borderRadius: 27,
    margin: 0,
    overflow: 'hidden',
  },
  androidButtonWrapperWithElevation: {
    borderRadius: 27,
    margin: 0,
    elevation: 3,
    overflow: 'hidden',
  },
  innerButtonWrapper: {
    height: DEFAULT_HEIGHT,
    width: '100%',
    borderRadius: 27,
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: Spacing.xl,
    paddingRight: Spacing.xl,
  },
  innerButtonWrapperShadow: {
    shadowColor: '#000000',
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 5,
  },
});

// Helper function to extract margin style from margin props
const getMarginStyle = (marginProps: {
  margin?: number;
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
  marginHorizontal?: number;
  marginVertical?: number;
}) => {
  const style: any = {};

  if (marginProps.margin !== undefined) {
    style.margin = marginProps.margin;
  }

  if (marginProps.marginTop !== undefined) {
    style.marginTop = marginProps.marginTop;
  }

  if (marginProps.marginBottom !== undefined) {
    style.marginBottom = marginProps.marginBottom;
  }

  if (marginProps.marginLeft !== undefined) {
    style.marginLeft = marginProps.marginLeft;
  }

  if (marginProps.marginRight !== undefined) {
    style.marginRight = marginProps.marginRight;
  }

  if (marginProps.marginHorizontal !== undefined) {
    style.marginHorizontal = marginProps.marginHorizontal;
  }

  if (marginProps.marginVertical !== undefined) {
    style.marginVertical = marginProps.marginVertical;
  }

  return style;
};

// Helper functions for dynamic styles
const getSharedButtonWrapperStyle = (
  borderRadius?: number,
  marginProps?: {
    margin?: number;
    marginTop?: number;
    marginBottom?: number;
    marginLeft?: number;
    marginRight?: number;
    marginHorizontal?: number;
    marginVertical?: number;
  }
): ViewStyle[] => {
  const styles_array: ViewStyle[] = [styles.sharedButtonWrapper];

  if (borderRadius) {
    styles_array.push({ borderRadius });
  }

  if (marginProps) {
    const marginStyle = getMarginStyle(marginProps);

    if (Object.keys(marginStyle).length > 0) {
      styles_array.push(marginStyle);
    }
  }

  return styles_array;
};

const getAndroidButtonWrapperStyle = (
  borderRadius?: number,
  marginProps?: {
    margin?: number;
    marginTop?: number;
    marginBottom?: number;
    marginLeft?: number;
    marginRight?: number;
    marginHorizontal?: number;
    marginVertical?: number;
  },
  secondary?: boolean
): ViewStyle[] => {
  const styles_array: ViewStyle[] = [
    secondary
      ? styles.androidButtonWrapper
      : styles.androidButtonWrapperWithElevation,
  ];

  if (borderRadius) {
    styles_array.push({ borderRadius });
  }

  if (marginProps) {
    const marginStyle = getMarginStyle(marginProps);

    if (Object.keys(marginStyle).length > 0) {
      styles_array.push(marginStyle);
    }
  }

  return styles_array;
};

const getInnerButtonWrapperStyle = (props: InnerButtonProps): ViewStyle[] => {
  const styles_array: ViewStyle[] = [
    styles.innerButtonWrapper,
    {
      height: props.height || DEFAULT_HEIGHT,
      width: props.circle ? props.height || DEFAULT_HEIGHT : ('100%' as const),
      borderRadius: props.borderRadius || 27,
      opacity: props.disabled ? 0.8 : 1,
      backgroundColor: props.backgroundColor,
      paddingLeft:
        props.paddingLeft !== undefined ? props.paddingLeft : Spacing.xl,
      paddingRight:
        props.paddingRight !== undefined ? props.paddingRight : Spacing.xl,
    },
  ];

  // Apply iOS shadow only on iOS and when not secondary
  if (Platform.OS === 'ios' && !props.secondary) {
    styles_array.push(styles.innerButtonWrapperShadow);
  }

  return styles_array;
};

interface InnerButtonProps {
  circle?: boolean;
  backgroundColor: string;
  borderRadius?: number;
  color?: string;
  iconColor?: string;
  disabled?: boolean;
  height?: number;
  icon?: IconName;
  iconSize?: number;
  isLoading?: boolean;
  secondary?: boolean;
  small?: boolean;
  title?: string;
  titleStyle?: TextStyle;
  paddingLeft?: number;
  paddingRight?: number;
}

const getInnerButtonStyle = (props: InnerButtonProps): ViewStyle => {
  const innerButtonStyle: ViewStyle = {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    ...(props.circle ? { width: '100%' } : { width: 'auto', maxWidth: '100%' }),
  };

  // if the icon isn't the default size, center it vertically with the text
  return !props.iconSize || props.iconSize === defaultIconSize
    ? innerButtonStyle
    : { ...innerButtonStyle, alignItems: 'center' };
};

const InnerButton = (props: InnerButtonProps) => (
  <View style={getInnerButtonWrapperStyle(props)}>
    {props.isLoading ? (
      <View
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <KitLoader small color={props.color} />
      </View>
    ) : null}
    <View
      style={{
        ...getInnerButtonStyle(props),
        ...(props.isLoading && { opacity: 0 }),
      }}
    >
      {props.icon ? (
        <KitIcon
          style={props.title ? { marginRight: Spacing.s } : undefined}
          name={props.icon}
          size={props.iconSize}
          color={props.iconColor || props.color}
        />
      ) : null}
      {props.title ? (
        <KitText
          semiBold
          fontSize={Platform.OS === 'web' ? 14 : 16}
          center
          color={props.color}
          numberOfLines={1}
          style={props.titleStyle}
        >
          {props.title}
        </KitText>
      ) : null}
    </View>
  </View>
);
